import React from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { 
  Package, LayoutDashboard, Truck, PlusCircle, ClipboardList, 
  FileText, LogOut, Building, User, ChevronRight, Phone, ShieldCheck
} from 'lucide-react';
import { Button } from '../ui/button';

export default function CustomerPortalLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();

  const customerInfoStr = localStorage.getItem('customer_info');
  const customer = customerInfoStr ? JSON.parse(customerInfoStr) : null;

  const handleLogout = () => {
    localStorage.removeItem('customer_token');
    localStorage.removeItem('customer_info');
    navigate('/customer-login');
  };

  const navItems = [
    { label: 'Dashboard', path: '/customer-portal/dashboard', icon: LayoutDashboard },
    { label: 'My Shipments', path: '/customer-portal/dockets', icon: Truck },
    { label: 'Request a Booking', path: '/customer-portal/request-booking', icon: PlusCircle, isHighlight: true },
    { label: 'Booking Inquiries', path: '/customer-portal/booking-requests', icon: ClipboardList },
    { label: 'Quotations & Estimates', path: '/customer-portal/estimates', icon: FileText },
    { label: 'KYC Compliance', path: '/customer-portal/kyc', icon: ShieldCheck },
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col font-ibm text-slate-900">
      {/* Top Navbar */}
      <header className="bg-slate-900 text-white sticky top-0 z-50 shadow-md border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo / Brand */}
            <div className="flex items-center gap-6">
              <Link to="/customer-portal/dashboard" className="flex items-center gap-2.5">
                <div className="w-9 h-9 bg-blue-600 rounded-xl flex items-center justify-center text-white shadow-md shadow-blue-500/20">
                  <Package className="w-5 h-5" />
                </div>
                <div>
                  <span className="font-bold text-lg font-manrope tracking-tight text-white block leading-none">
                    LogisticsHub
                  </span>
                  <span className="text-[10px] uppercase font-bold tracking-wider text-blue-400 block pt-0.5">
                    Client Portal
                  </span>
                </div>
              </Link>

              {/* Desktop Nav Links */}
              <nav className="hidden md:flex items-center gap-1.5 ml-4">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-semibold transition-all ${
                        item.isHighlight
                          ? 'bg-blue-600 text-white hover:bg-blue-500 shadow-sm'
                          : isActive
                          ? 'bg-white/10 text-white'
                          : 'text-slate-300 hover:text-white hover:bg-white/5'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      <span>{item.label}</span>
                    </Link>
                  );
                })}
              </nav>
            </div>

            {/* Right Side: Client Profile & Logout */}
            <div className="flex items-center gap-3">
              <div className="hidden sm:flex flex-col text-right">
                <span className="text-xs font-bold text-white font-manrope truncate max-w-[200px]">
                  {customer?.business_name || 'Valued Client'}
                </span>
                <span className="text-[11px] text-slate-400 truncate max-w-[200px]">
                  {customer?.branch_name ? `Branch: ${customer.branch_name}` : (customer?.mobile ? `+91 ${customer.mobile}` : 'Active Account')}
                </span>
              </div>

              <div className="w-9 h-9 bg-slate-800 border border-slate-700 rounded-xl flex items-center justify-center text-blue-400">
                <Building className="w-4 h-4" />
              </div>

              <Button
                variant="ghost"
                onClick={handleLogout}
                title="Sign Out"
                className="text-slate-400 hover:text-red-400 hover:bg-red-500/10 h-9 w-9 p-0 rounded-xl"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Subnav */}
        <div className="md:hidden flex items-center overflow-x-auto px-4 py-2 bg-slate-950/60 border-t border-slate-800 gap-1.5 no-scrollbar">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap shrink-0 ${
                  item.isHighlight
                    ? 'bg-blue-600 text-white'
                    : isActive
                    ? 'bg-white/10 text-white font-bold'
                    : 'text-slate-400'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 py-4 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between text-xs text-slate-500 gap-2">
          <span>&copy; {new Date().getFullYear()} LogisticsHub. All client data is private and encrypted.</span>
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1 text-slate-600">
              <Phone className="w-3.5 h-3.5 text-emerald-600" />
              Support Desk: +91 98765 43210
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}
